import { useNavigate } from "react-router-dom";
import { staleLeadsData, topSourceData, agentActivityData, responseAlertData } from "@/mocks/reportsData";

export default function ActivityIntelligence() {
  const navigate = useNavigate();
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Activity Intelligence</h3>
        <p className="text-xs text-gray-500 mt-0.5">Smart alerts and insights from your team activity</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* CARD 1 — Stale Leads */}
        <div className="border-l-4 border-red-500 rounded-xl bg-white border border-gray-200 p-4">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-time-line text-red-600 text-lg" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Stale Leads</p>
              <p className="text-3xl font-bold text-red-600">{staleLeadsData.length}</p>
              <p className="text-sm text-gray-500">No activity in last 7 days</p>
            </div>
          </div>

          <div className="space-y-1.5 mb-3">
            {staleLeadsData.map((lead) => (
              <div key={lead.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                  <span className="text-gray-700 truncate">{lead.name}</span>
                  <span className="text-gray-400 whitespace-nowrap">· {lead.days} days</span>
                  <span className="text-gray-400 whitespace-nowrap">· {lead.stage}</span>
                </div>
                <button className="text-indigo-600 hover:text-indigo-700 text-xs font-medium flex-shrink-0 ml-2 whitespace-nowrap">
                  Follow up
                </button>
              </div>
            ))}
          </div>

          <button
            onClick={() => navigate("/leads?filter=stale")}
            className="w-full py-2 rounded-lg border border-indigo-300 text-indigo-600 hover:bg-indigo-50 text-xs font-medium transition-colors whitespace-nowrap"
          >
            View All 23
          </button>
        </div>

        {/* CARD 2 — Top Source */}
        <div className="border-l-4 border-green-500 rounded-xl bg-white border border-gray-200 p-4">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-line-chart-line text-green-600 text-lg" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Top Source This Month</p>
              <p className="text-2xl font-bold text-green-600">{topSourceData.source}</p>
              <p className="text-sm text-gray-500">{topSourceData.leads} leads · {topSourceData.conversion}% conversion</p>
            </div>
          </div>

          <div className="space-y-2">
            {/* IndiaMart bar - full width */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600 w-16 flex-shrink-0 whitespace-nowrap">{topSourceData.source}</span>
              <div className="flex-1 min-w-0">
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full" style={{ width: "100%" }} />
                </div>
              </div>
              <span className="text-xs text-gray-700 w-12 text-right flex-shrink-0 whitespace-nowrap">{topSourceData.conversion}%</span>
            </div>
            {topSourceData.competitors.map((c) => {
              const pct = (c.rate / topSourceData.conversion) * 100;
              return (
                <div key={c.name} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-16 flex-shrink-0 whitespace-nowrap">{c.name}</span>
                  <div className="flex-1 min-w-0">
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-green-400 rounded-full" style={{ width: `${Math.max(pct, 8)}%` }} />
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 w-12 text-right flex-shrink-0 whitespace-nowrap">{c.rate}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* CARD 3 — Agent Activity */}
        <div className="border-l-4 border-indigo-500 rounded-xl bg-white border border-gray-200 p-4">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-star-line text-indigo-600 text-lg" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Most Active Agent</p>
              <p className="text-xl font-bold text-indigo-600">{agentActivityData.agent}</p>
              <p className="text-sm text-gray-500">{agentActivityData.calls} calls · {agentActivityData.emails} follow-ups this week</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 whitespace-nowrap">
              {agentActivityData.calls} Calls
            </span>
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 whitespace-nowrap">
              {agentActivityData.emails} Emails
            </span>
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 whitespace-nowrap">
              {agentActivityData.whatsapp} WhatsApp
            </span>
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 whitespace-nowrap">
              {agentActivityData.notes} Notes
            </span>
          </div>
        </div>

        {/* CARD 4 — Response Alert */}
        <div className="border-l-4 border-amber-500 rounded-xl bg-white border border-gray-200 p-4">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-notification-3-line text-amber-600 text-lg" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Slow Response Alert</p>
              <p className="text-2xl font-bold text-amber-600">{responseAlertData.count} leads</p>
              <p className="text-sm text-gray-500">Not contacted within 2 hours</p>
            </div>
          </div>

          <div className="space-y-1.5 mb-3">
            {responseAlertData.leads.map((lead) => (
              <div key={lead.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                  <span className="text-gray-700 truncate">{lead.name}</span>
                  <span className="text-gray-400 whitespace-nowrap">· {lead.hoursAgo} hrs ago</span>
                  <span className="text-gray-400 whitespace-nowrap hidden sm:inline">· {lead.stage}</span>
                  <span className="text-gray-400 whitespace-nowrap hidden sm:inline">· {lead.assigned}</span>
                </div>
              </div>
            ))}
          </div>

          <button className="w-full py-2 rounded-lg border border-amber-600 text-amber-600 hover:bg-amber-50 text-xs font-medium transition-colors whitespace-nowrap">
            Assign &amp; Alert
          </button>
        </div>
      </div>
    </div>
  );
}